'use client';

import { motion } from 'framer-motion';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { Button } from '@/components/ui/button';
import { components } from '@/types/strapi';

export default function DLTRegistration({
	heading = 'DLT Registration',
	sections = [
		{
			title: 'What Is DLT Registration?',
			content:
				'To ensure compliance and secure message delivery, businesses are required to complete DLT registration before sending commercial messages. DLT, or Distributed Ledger Technology, is a decentralized database system that securely records transactions across multiple nodes without the need for a central authority.',
		},
		{
			title: 'Why Is DLT Registration Important?',
			content:
				'DLT registration is mandatory for businesses to legally send promotional, transactional, and OTP messages in compliance with telecom regulations. It helps in preventing spam and ensuring that only registered entities can send commercial messages, safeguarding customer trust and reducing message fraud.',
		},
		{
			title: 'How To Complete DLT Registration?',
			content:
				"To get started, businesses must register with a telecom operator's DLT platform by submitting their business details and message templates. Once registered, you'll be able to send bulk SMS to your customers through a secure and compliant system. As a bonus, Mtalkz offers free SMS credits for businesses that complete their DLT registration, helping you kick-start your campaigns right away.",
		},
	],
	ctaText = 'Learn More About DLT Registration',
	ctaLink = '#dlt-registration',
	backgroundColor = 'bg-gradient-to-b from-gray-50 to-white',
	isFirst = false,
}: components['schemas']['DynamicZoneDltRegistrationComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = sectionHeadingTag(isFirst);
	return (
		<section
			id="dlt-registration"
			data-testid="dlt-section"
			className={`dlt-registration-section px-3 py-8 sm:px-4 lg:px-6 ${backgroundColor}`}>
			<noscript>
				<style>{`
					.dlt-registration-section h2[style],
					.dlt-registration-section h3[style],
					.dlt-registration-section h4[style],
					.dlt-registration-section p[style],
					.dlt-registration-section div[style*='opacity'],
					.dlt-registration-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mx-auto max-w-7xl">
				<div
					data-testid="main-grid"
					className="grid grid-cols-1 gap-4 lg:grid-cols-5">
					{/* Left Column - Section Cards */}
					<div
						data-testid="left-column"
						className="grid auto-rows-min gap-4 md:auto-rows-[min(220px,auto)] lg:col-span-3 lg:auto-rows-[160px]">
						{sections?.map((section, index) => (
							<motion.div
								key={index}
								data-testid="section-card"
								initial={{ opacity: 0, x: -20 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{ once: true }}
								transition={{ delay: 0.1 * index }}
								className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-all duration-300 hover:border-indigo-100 hover:shadow-lg">
								<div className="flex h-full gap-3">
									<div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-indigo-50 transition-colors group-hover:bg-indigo-100">
										<span className="text-sm font-bold text-indigo-600">
											{index + 1}
										</span>
									</div>
									<div className="min-w-0 flex-1">
										<h3 className="mb-1.5 text-base font-semibold text-gray-900 transition-colors group-hover:text-indigo-600">
											{section.title}
										</h3>
										<p className="text-sm leading-relaxed text-gray-600">
											{section.content}
										</p>
									</div>
								</div>
							</motion.div>
						))}
					</div>

					{/* Right Column - Feature Card and Info Cards */}
					<div
						data-testid="right-column"
						className="grid auto-rows-min gap-4 sm:auto-rows-[160px] lg:col-span-2">
						{/* Main Feature Card */}
						<motion.div
							data-testid="feature-card"
							initial={{ opacity: 0, x: 20 }}
							whileInView={{ opacity: 1, x: 0 }}
							viewport={{ once: true }}
							className="row-span-2 overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-700 p-5 text-white shadow-xl sm:p-6">
							<div className="relative h-full">
								{/* Decorative Elements */}
								<div className="pointer-events-none absolute inset-0 overflow-hidden">
									<div className="absolute right-0 top-0 h-32 w-32 -translate-y-1/2 translate-x-1/2 rounded-full bg-white/10 blur-lg" />
									<div className="absolute bottom-0 left-0 h-24 w-24 -translate-x-1/2 translate-y-1/2 rounded-full bg-white/10 blur-lg" />
								</div>

								<div className="relative flex h-full flex-col">
									<div className="mb-4">
										<div className="inline-flex rounded-xl bg-white/10 p-2.5 backdrop-blur-sm">
											<svg
												className="h-6 w-6 text-white"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor">
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth="2"
													d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
												/>
											</svg>
										</div>
									</div>

									<SectionHeading className="mb-3 text-2xl font-bold tracking-tight sm:text-3xl">
										{heading}
									</SectionHeading>
									<p className="mb-6 text-base leading-relaxed text-white/90">
										Secure your business communications with
										compliant messaging through DLT
										registration.
									</p>
									{ctaText && ctaLink && (
										<div className="mt-auto">
											<Button
												asChild
												variant="secondary"
												size="lg"
												className="w-full rounded-full bg-white font-medium text-indigo-600 shadow-lg transition-all duration-300 hover:scale-105 hover:bg-gray-50 hover:shadow-xl sm:w-auto">
												<a href={ctaLink}>{ctaText}</a>
											</Button>
										</div>
									)}
								</div>
							</div>
						</motion.div>

						{/* Info Cards Grid */}
						<div
							data-testid="info-cards-grid"
							className="grid grid-cols-1 gap-3 sm:grid-cols-2">
							{[
								{
									title: 'Free SMS Credits',
									description:
										'Get complimentary SMS credits after completing your DLT registration.',
									icon: (
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									),
								},
								{
									title: 'Quick Setup Process',
									description:
										'Fast and easy registration with expert guidance at every step.',
									icon: (
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M13 10V3L4 14h7v7l9-11h-7z"
										/>
									),
								},
							].map((card, index) => (
								<motion.div
									key={index}
									data-testid="info-card"
									initial={{ opacity: 0, y: 20 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: true }}
									transition={{ delay: 0.2 + index * 0.1 }}
									className="group relative overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-br from-white to-indigo-50/50 p-3.5 shadow-sm transition-all duration-300 hover:shadow-lg">
									<div className="flex h-full flex-col gap-2.5">
										<div className="inline-flex rounded-lg bg-indigo-100 p-2 transition-colors group-hover:bg-indigo-200">
											<svg
												className="h-5 w-5 text-indigo-600"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor">
												{card.icon}
											</svg>
										</div>
										<div>
											<h4 className="mb-1 text-sm font-semibold text-gray-900 transition-colors group-hover:text-indigo-600">
												{card.title}
											</h4>
											<p className="text-xs leading-relaxed text-gray-600">
												{card.description}
											</p>
										</div>
									</div>
								</motion.div>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
